// TODO: see if we need to clean this up and add some more tests and make sure the existing ones still

/**
 * Client-side PDF-to-Markdown extraction built on pdfjs-dist.
 *
 * Primary API:
 * - pdfDownloadUrlToMarkdown(downloadUrl, options): fetches a PDF URL and returns extracted Markdown.
 * - arrayBufferToPdfMarkdown(arrayBuffer, options): converts an already-fetched PDF buffer.
 *
 * Options:
 * - yTolerance: max Y-coordinate difference, in PDF units, for text items to be treated as one line.
 *   Increase this if words on the same visual line are being split across lines.
 * - minGap: minimum horizontal gap, in PDF units, before inserting a space between adjacent text items.
 *   Decrease this if words are being incorrectly joined; increase it if words are getting extra spaces.
 * - paragraphIndentThreshold: optional positive X-coordinate delta that treats an indented next line
 *   as a new paragraph and prefixes it with a tab character.
 * - pdfjs: optional object spread into pdfjs.getDocument({ ... }) for advanced pdf.js loading settings.
 * - fetchFn: optional fetch-compatible function used only by pdfDownloadUrlToMarkdown, useful for tests.
 * - lineDelimiter: uses a space by default but can use a \n for more table looking stuff.
 *
 * Default constants:
 * - DEFAULT_LINE_Y_TOLERANCE = 3: forgiving enough for slight PDF baseline drift on one line.
 * - DEFAULT_PARAGRAPH_INDENT_THRESHOLD = 12: treats a meaningful first-line indent as a paragraph
 *   break while ignoring small PDF extraction position drift.
 *
 * This intentionally returns plain readable Markdown text. It does not infer headings, tables,
 * bullets, or visible page markers because those are lossy and PDF-specific heuristics.
 */
const DEFAULT_LINE_Y_TOLERANCE = 3;
const DEFAULT_PARAGRAPH_INDENT_THRESHOLD = 12;

const getItemX = (item) => item?.transform?.[4] ?? 0;
const getItemY = (item) => item?.transform?.[5] ?? 0;
const getItemWidth = (item) => item?.width ?? 0;
const getItemHeight = (item) => item?.height ?? Math.abs(item?.transform?.[3] ?? 0) ?? 0;
const getLineX = (line) => {
	if (Number.isFinite(line?.x)) return line.x;
	let minX = Infinity;
	for (const item of line?.items || []) minX = Math.min(minX, getItemX(item));
	return minX === Infinity ? 0 : minX;
};

const normalizeText = (text = "") => text.replace(/\s+/g, " ");

const createLine = (item) => ({
	y: getItemY(item),
	x: getItemX(item),
	height: getItemHeight(item),
	items: [item]
});

const addItemToLine = (line, item) => {
	const itemY = getItemY(item);
	line.y = (line.y * line.items.length + itemY) / (line.items.length + 1);
	line.x = Math.min(getLineX(line), getItemX(item));
	line.height = Math.max(line.height, getItemHeight(item));
	line.items.push(item);
};

export const textContentItemsToLines = (items = [], options = {}) => {
	const yTolerance = options.yTolerance ?? DEFAULT_LINE_Y_TOLERANCE;
	const lines = [];

	for (const item of items) {
		if (!normalizeText(item?.str).trim()) continue;

		const itemY = getItemY(item);
		const line = lines.find((candidate) => Math.abs(candidate.y - itemY) <= yTolerance);

		if (line) {
			addItemToLine(line, item);
		} else {
			lines.push(createLine(item));
		}
	}

	return lines.sort((a, b) => b.y - a.y);
};

export const lineToMarkdownText = (line, options = {}) => {
	const items = [...(line?.items || [])].sort((a, b) => getItemX(a) - getItemX(b));
	const minGap = options.minGap ?? 1.5;
	let text = "";
	let previousRight = null;
	let previousHeight = 0;

	for (const item of items) {
		const itemText = normalizeText(item.str);
		if (!itemText.trim()) continue;

		const x = getItemX(item);
		const width = getItemWidth(item);
		const height = getItemHeight(item);

		if (text && previousRight !== null) {
			const gap = x - previousRight;
			const gapThreshold = Math.max(minGap, previousHeight * 0.2);
			const needsSpace = gap > gapThreshold && !/\s$/.test(text) && !/^\s/.test(itemText);
			if (needsSpace) text += " ";
		}

		text += itemText;
		previousRight = x + width;
		previousHeight = height;
	}

	return text.trim();
};

export const linesToMarkdown = (lines = [], options = {}) => {
	const paragraphIndentThreshold =
		options.paragraphIndentThreshold ?? DEFAULT_PARAGRAPH_INDENT_THRESHOLD;
	const minLineX = lines.reduce((min, line) => Math.min(min, getLineX(line)), Infinity);
	const leftMargin = Number.isFinite(minLineX) ? minLineX : 0;
	const output = [];
	let previousLine = null;
	const lineDelimiter = options.lineDelimiter || " ";

	for (const line of lines) {
		const lineText = lineToMarkdownText(line, options);
		if (!lineText) continue;

		if (previousLine) {
			const indent = getLineX(line) - leftMargin;
			const startsIndentedParagraph =
				paragraphIndentThreshold > 0 && indent >= paragraphIndentThreshold;
			output.push(startsIndentedParagraph ? "\n\n" : lineDelimiter);
			if (startsIndentedParagraph) output.push("\t");
		}

		output.push(lineText);
		previousLine = line;
	}

	return output.join("");
};

export const pagesToMarkdown = (pages = [], options = {}) =>
	linesToMarkdown(pages.flat(), options).trim();

const configurePdfJsWorker = (pdfjs) => {
	if (typeof window === "undefined") return;
	if (pdfjs.GlobalWorkerOptions.workerSrc) return;

	pdfjs.GlobalWorkerOptions.workerSrc = new URL(
		"pdfjs-dist/legacy/build/pdf.worker.mjs",
		import.meta.url
	).href;
};

export const arrayBufferToPdfMarkdown = async (arrayBuffer, options = {}) => {
	const pdfjs = await import("pdfjs-dist/legacy/build/pdf.mjs");
	configurePdfJsWorker(pdfjs);

	const loadingTask = pdfjs.getDocument({
		data: new Uint8Array(arrayBuffer),
		disableWorker: typeof window === "undefined",
		...options.pdfjs
	});
	const pdf = await loadingTask.promise;
	const pages = [];
	const pageImages = [];

	const allLines = [];

	for (let pageNumber = 1; pageNumber <= pdf.numPages; pageNumber += 1) {
		const page = await pdf.getPage(pageNumber);
		const textContent = await page.getTextContent();
		const lines = textContentItemsToLines(textContent.items, options);
		allLines.push(...lines);
		if (lines.length) pages.push(lines);

		const viewport = page.getViewport({ scale: 3 });

		const canvas = document.createElement("canvas");
		const context = canvas.getContext("2d");

		canvas.width = viewport.width;
		canvas.height = viewport.height;

		await page.render({
			canvasContext: context,
			viewport
		}).promise;

		const imageDataUrl = canvas.toDataURL("image/jpeg", 0.9);

		pageImages.push(imageDataUrl);
	}

	const allTexts = [];
	let ly = 0;

	for (const l of allLines) {
		const { x, y } = l;
		let str = l.items.map(i => i.str.trim()).filter(Boolean).join(' ');
		const dy = Math.round(ly - y);
		allTexts.push({ str, x: Math.round(x), y: Math.round(y), dy });
		ly = y;
	}

	const lineDelimiter = options.lineDelimiter || ' ';
	const paragraphDelimiter = options.paragraphDelimiter || '\n\n';

	const yDeltas = [...new Set(allTexts.map(x => x.dy).filter(x => x > 0))].sort((a, b) => a - b);
	const xValues = [...new Set(allTexts.map(x => x.x).filter(x => x > 0))].sort((a, b) => a - b);

	let text = '';
	for (const t of allTexts) {
		const yi = Math.max(yDeltas.indexOf(t.dy), 0);
		const xi = Math.max(xValues.indexOf(t.x), 0);
		const d = (yi > 0 || xi > 0) ? '[[paragraphDelimiter]]' : lineDelimiter;
		text += (d + t.str);
	}

	text = text.split('[[paragraphDelimiter]]').map(p => p.trim()).join(paragraphDelimiter);

	return { text, pageImages };

};

export const pdfDownloadUrlToMarkdown = async (downloadUrl, options = {}) => {
	if (!downloadUrl) throw new Error("pdfDownloadUrlToMarkdown: downloadUrl is required");

	const fetchFn = options.fetchFn ?? fetch;
	const response = await fetchFn(downloadUrl);

	if (!response.ok) {
		throw new Error(`Failed to fetch PDF: ${response.status} ${response.statusText}`);
	}

	const contentType = response.headers?.get?.("content-type") || "";
	if (contentType && !contentType.includes("application/pdf") && !contentType.includes("octet-stream")) {
		console.warn(`pdfDownloadUrlToMarkdown: expected PDF content type, received ${contentType}`);
	}

	const arrayBuffer = await response.arrayBuffer();
	return arrayBufferToPdfMarkdown(arrayBuffer, options);
};
